import { customInstance } from '@/shared/api/mutator/custom-instance';
import {
    Collateral,
    CollateralDetailResponse,
    CollateralDocument,
    CollateralSearchParams,
    LinkCollateralRequest,
    LoanCollateralLink,
    Page,
} from '../types/collateral.types';

// ─── Standalone collateral registry ─────────────────────────────────────────
// Base path: /api/collaterals — mirrors CollateralController.java

export const searchCollaterals = (
    params: CollateralSearchParams = {},
): Promise<Page<Collateral>> =>
    customInstance({
        url: '/api/collaterals',
        method: 'GET',
        params,
    });

export const getCollateralDetail = (id: number): Promise<CollateralDetailResponse> =>
    customInstance({
        url: `/api/collaterals/${id}`,
        method: 'GET',
    });

export const createCollateral = (body: Collateral): Promise<Collateral> =>
    customInstance({
        url: '/api/collaterals',
        method: 'POST',
        data: body,
    });

export const updateCollateral = (id: number, body: Collateral): Promise<Collateral> =>
    customInstance({
        url: `/api/collaterals/${id}`,
        method: 'PUT',
        data: body,
    });

export const deleteCollateral = (id: number): Promise<void> =>
    customInstance({
        url: `/api/collaterals/${id}`,
        method: 'DELETE',
    });

// ─── Collateral documents ───────────────────────────────────────────────────

export const listCollateralDocuments = (
    collateralId: number,
): Promise<CollateralDocument[]> =>
    customInstance({
        url: `/api/collaterals/${collateralId}/documents`,
        method: 'GET',
    });

export const addCollateralDocument = (
    collateralId: number,
    body: CollateralDocument,
): Promise<CollateralDocument> =>
    customInstance({
        url: `/api/collaterals/${collateralId}/documents`,
        method: 'POST',
        data: body,
    });

export const deleteCollateralDocument = (
    collateralId: number,
    documentId: number,
): Promise<void> =>
    customInstance({
        url: `/api/collaterals/${collateralId}/documents/${documentId}`,
        method: 'DELETE',
    });

// ─── Loan ↔ collateral links ────────────────────────────────────────────────
// Base path: /api/loans/{branch}/{glNo}/{acNo}/{cycle}/collateral-links
// Mirrors LoanCollateralLinkController.java

const linkBase = (branch: string, glNo: string, acNo: string, cycle: number) =>
    `/api/loans/${encodeURIComponent(branch)}/${encodeURIComponent(glNo)}/${encodeURIComponent(acNo)}/${cycle}/collateral-links`;

export const listLoanCollateralLinks = (
    branch: string,
    glNo: string,
    acNo: string,
    cycle: number,
): Promise<LoanCollateralLink[]> =>
    customInstance({ url: linkBase(branch, glNo, acNo, cycle), method: 'GET' });

export const linkCollateralToLoan = (
    branch: string,
    glNo: string,
    acNo: string,
    cycle: number,
    body: LinkCollateralRequest,
): Promise<LoanCollateralLink> =>
    customInstance({
        url: linkBase(branch, glNo, acNo, cycle),
        method: 'POST',
        data: body,
    });

export const unlinkCollateralFromLoan = (
    branch: string,
    glNo: string,
    acNo: string,
    cycle: number,
    linkId: number,
): Promise<void> =>
    customInstance({
        url: `${linkBase(branch, glNo, acNo, cycle)}/${linkId}`,
        method: 'DELETE',
    });
